// Alta de vendedor: formulario y body del alta orquestada de `retail-org` (Cognito + RBAC + padrón).
import { z } from 'zod';
import { PermissionScope } from '@fiado/type-kit/bin/platformRbac/enums/PermissionScope';
import { EmploymentTypeEnum } from '@fiado/type-kit/bin/retailOrg/enums/EmploymentTypeEnum';
import type { ProvisionRetailUserRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/ProvisionRetailUserRequest';
import { StoreStatusEnum } from '@fiado/type-kit/bin/retailOrg/enums/StoreStatusEnum';
import type { Store } from '@/api/services/retail-org/retail-org.client';
import { emailField, optionalMxPhoneField } from '@/lib/formValidators';
import { toPhoneDigits } from '@/lib/phoneMx';

/** Roles del catálogo RBAC del tenant; el alta solo asigna `vendedor`, con alcance de tienda (LEVEL_3). */
export const SELLER_ROLE_ID = 'vendedor';
export const STORE_MANAGER_ROLE_ID = 'store_manager';

/** El mismo tier que asigna `retail-org` al vendedor que llega por integración. */
export const DEFAULT_SELLER_TIER = 'seller';

export const newSellerSchema = z.object({
    displayName: z.string().trim().min(1, 'Escribe el nombre.'),
    email: emailField(),
    phone: optionalMxPhoneField,
    storeId: z.string().min(1, 'Elige la tienda.'),
});

export type NewSellerForm = z.infer<typeof newSellerSchema>;

export const EMPTY_SELLER_FORM: NewSellerForm = {
    displayName: '',
    email: '',
    phone: '',
    storeId: '',
};

/** Tiendas activas de la cadena para el select, en orden alfabético. */
export function storeSelectOptions(stores: readonly Store[]): { value: string; label: string }[] {
    return stores
        .filter((store) => store.status === StoreStatusEnum.ACTIVE)
        .map((store) => ({ value: store.storeId, label: store.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es-MX'));
}

/** El grant de RBAC va a la tienda elegida; la zona del padrón es la de esa tienda. */
export function toProvisionRequest(
    form: NewSellerForm,
    store: Pick<Store, 'storeId' | 'zoneId'> | undefined,
): ProvisionRetailUserRequest {
    const phone = toPhoneDigits(form.phone);
    return {
        email: form.email.trim().toLowerCase(),
        displayName: form.displayName.trim(),
        roleId: SELLER_ROLE_ID,
        scope: PermissionScope.LEVEL_3,
        scopeRef: form.storeId,
        homeStoreId: form.storeId,
        employmentType: EmploymentTypeEnum.EMPLOYEE,
        commissionTier: DEFAULT_SELLER_TIER,
        zoneId: store?.zoneId ?? null,
        ...(phone === '' ? {} : { phone }),
    };
}
